/**
 * Which Google Maps Platform product a usage event bills against. Lets us split O2
 * counts by the expensive SKU, not just by where in the app it fired.
 */
export type PlacesApi =
  | 'autocomplete' // Places Autocomplete predictions (per-request billed)
  | 'autocomplete_session_start' // not a billed call: one event per opened autocomplete session, to derive fetches-per-session vs GCP request_count
  | 'geocoding' // Geocoding API (address <-> coordinates)
  | 'place_details' // Place Details
  | 'nearby_search'; // Places Nearby Search (New) — Pro SKU

interface PlacesUsageEvent {
  /**
   * Stable identifier of the call site, e.g. `worker_explorer_locality_search`,
   * `employer_restaurant_onboarding_venue`. Drives the "which point of the app
   * consumes the most" breakdown in OpenObserve.
   */
  surface: string;
  api: PlacesApi;
  /** Extra context (e.g. filter types, query length) — optional, kept small. */
  meta?: Record<string, string | number | boolean>;
}

/**
 * Record one Google Maps Platform usage event to OpenObserve, tagged by call site.
 *
 * Emits a `places_api_usage` log to the O2 logs stream via the already-initialized
 * browser-logs SDK, so we can aggregate consumption per surface/api over a window:
 *
 *   SELECT surface, api, count(*) FROM "<logs-stream>"
 *   WHERE event = 'places_api_usage'
 *   GROUP BY surface, api ORDER BY 3 DESC
 *
 * Fire-and-forget: no-op on the server and inert until `initOpenObserveRum` has
 * activated the SDK. Never throws — telemetry must not break the app.
 */
export function trackPlacesUsage({ surface, api, meta }: PlacesUsageEvent): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Load the browser-only logs SDK lazily so this module never pulls it into the server
  // bundle (its optional `@openobserve/browser-rum` dep chains to the ESM-only `uuid`,
  // which crashes SSR). The chunk is already cached once RUM/logs init in the browser.
  import('@openobserve/browser-logs')
    .then(({ openobserveLogs }) => {
      openobserveLogs.logger.info('places_api_usage', {
        event: 'places_api_usage',
        surface,
        api,
        ...meta,
      });
    })
    .catch(() => {
      // Swallow: SDK not initialized / logging failure must never surface to the user.
    });
}
